'use client';

import Image from '@/components/ui/CspImage';
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { ArrowLeft } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

interface StaticPageLayoutProps {
  title: string;
  lastUpdated?: string;
  children: React.ReactNode;
}

export default function StaticPageLayout({ title, lastUpdated, children }: StaticPageLayoutProps) {
  const t = useTranslations();
  return (
    <div className="landing-theme min-h-screen bg-background font-body">
      {/* Header - matches landing page nav */}
      <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
              <span className="text-sm font-medium">{t('staticPage.backToHome')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="block dark:hidden w-auto h-[2.25rem]"
                priority
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="hidden dark:block w-auto h-[2.25rem]"
                priority
              />
            </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-12 px-6 bg-gradient-to-b from-background via-secondary/30 to-background grain-overlay">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tight">{title}</h1>
          {lastUpdated && (
            <p className="text-sm text-muted-foreground">{t('staticPage.lastUpdated', { date: lastUpdated })}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <main id="main-content" className="max-w-4xl mx-auto px-6 py-16">
        {children}
      </main>

      {/* Footer - matches landing page footer style */}
      <footer className="border-t border-border bg-card py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={120}
                height={32}
                className="block dark:hidden w-auto h-[2rem]"
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={120}
                height={32}
                className="hidden dark:block w-auto h-[2rem]"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link href="/tools" className="link-underline hover:text-primary transition-colors">Tools</Link>
              <Link href="/guide" className="link-underline hover:text-primary transition-colors">{t('footer.guide')}</Link>
              <Link href="/blog" className="link-underline hover:text-primary transition-colors">Blog</Link>
              <Link href="/mcp" className="link-underline hover:text-primary transition-colors">MCP</Link>
              <Link href="/documentation/platform" className="link-underline hover:text-primary transition-colors">Docs</Link>
              <Link href="/privacy" className="link-underline hover:text-primary transition-colors">{t('footer.privacy')}</Link>
              <Link href="/subprocessors" className="link-underline hover:text-primary transition-colors">{t('footer.subprocessors')}</Link>
              <Link href="/terms" className="link-underline hover:text-primary transition-colors">{t('footer.terms')}</Link>
              <Link href="/cookies" className="link-underline hover:text-primary transition-colors">{t('footer.cookies')}</Link>
              <Link href="/accessibility" className="link-underline hover:text-primary transition-colors">{t('footer.accessibility')}</Link>
            </div>
            <div className="text-sm text-muted-foreground">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
